import { CustomClaimValue, Device, DeviceMediaRequest, formatRecordingPeriodDuration, getMediaRequestStatusesDisplayName, MediaRequests, MediaRequestSortKey, MediaRequestStatuses, Icons, MediaRequestSource, getMediaRequestSourceDisplayName, Users, TimePeriod } from "@app/shared";
import { addMilliseconds, formatRelative, startOfMinute, subHours } from "date-fns";
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { ReactNode, useCallback, useEffect, useState, useMemo } from "react";
import { useSessionStorage } from "react-use";
import { DataInfoCard, EditDeviceMediaRequest } from ".";
import copy from "copy-to-clipboard";
import { toastInfo, toastSuccess, toastWarning, debounce, formatByteSize } from "../../helpers";
import { useConfirmDialog, useFacilityTZ, useFetch, useHasClaim, useMediaRequestsHub } from "../../hooks";
import { MediaRequestJsonLink } from "./MediaRequestJsonLink";
import { useEditModelDialog } from "../../hooks/useEditModelDialog";
import { HoverTooltip } from "./HoverTooltip";
import { IconLink } from "./IconLink";
import { MediaRequestDownloadLinks } from "./MediaRequestDownloadLinks";
import { maxNoticeSeverity, visibleNotices } from "./mediaNoticeHelpers";
import { PageContainer } from "./PageContainer";
import { SortableTable, InfiniteScrollOptions } from "./SortableTable";
import { FilterOption } from "./Filter";
import { IconType } from "react-icons";

type DeviceMediaRequestsViewProps = {
    device: Device;
    onChange?: (device: Device) => void;
};

type MappedDeviceMediaRequest = DeviceMediaRequest & { id: string };

type InfiniteScrollQuery = {
    limit: number;
    sortKey?: MediaRequestSortKey;
    sortDirection?: "asc" | "desc";
    filters?: FilterOption<MappedDeviceMediaRequest>[];
    cursor?: string;
};

export const DeviceMediaRequestsView: React.FC<DeviceMediaRequestsViewProps> = ({ device, onChange }) => {
    const [earliest, , earliestHelper] = useFetch<TimePeriod>(() => (device ? (MediaRequests.getEarliestVideo(device?.id) as any) : Promise.resolve(undefined)), [device]);
    // Name-only directory, readable by every member of the org. getOrgUsers needs the ManageUsers claim,
    // so for an ordinary member it 403'd and left this undefined — which made the "Requested By" column
    // below fall through to its "Administrator" fallback for requests the viewer had made themselves.
    const [orgUsers, ,] = useFetch(() => (device ? Users.getOrgMembers(device.organizationId) : Promise.resolve(undefined)), [device]);
    const editDialog = useEditModelDialog(DeviceMediaRequest);
    const [hasBeenWarned, setHasBeenWarned] = useSessionStorage(`device_${device?.id}_media_request_dvr_warning_shown`, false);
    const confirm = useConfirmDialog();
    const [facilityTZ, , { isLoading: isFacilityTZLoading }] = useFacilityTZ(device?.facilityId!, true);
    const isSuperAdmin = useHasClaim(CustomClaimValue.SuperAdmin); // gates super-admin-only notices (e.g. missing support files)
    const [allUsers, ,] = useFetch(() => (isSuperAdmin ? Users.getAllUsers() : Promise.resolve(undefined)), [isSuperAdmin]);

    const [requests, setRequests] = useState<DeviceMediaRequest[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [nextCursor, setNextCursor] = useState<string | undefined>();

    const [query, setQuery] = useState<InfiniteScrollQuery>({
        limit: 50,
        sortKey: MediaRequestSortKey.RequestDate,
        sortDirection: "desc",
    });

    useEffect(() => {
        if (!hasBeenWarned && device?.enableDvrRecording === false) {
            toastWarning("DVR Disabled", "Device DVR is currently disabled. Media Requests for video require DVR", 30000);
            setHasBeenWarned(true);
        }
    }, [hasBeenWarned, setHasBeenWarned, device]);

    const handleRequestsUpdate = useCallback((updatedRequests: DeviceMediaRequest[]) => {
        setRequests(prev =>
            prev.map(r => updatedRequests.find(u => u.id === r.id) ?? r)
        );
    }, [setRequests]);

    useMediaRequestsHub(device.id, handleRequestsUpdate);

    const statusFilterOptions = Object.values(MediaRequestStatuses)
        .filter((value) => typeof value === "number")
        .map((value) => ({
            value: value.toString(),
            label: getMediaRequestStatusesDisplayName(value as MediaRequestStatuses) ?? "",
        }));

    const sourceFilterOptions = Object.values(MediaRequestSource)
        .filter((value) => typeof value === "number")
        .map((value) => ({
            value: value.toString(),
            label: getMediaRequestSourceDisplayName(value as MediaRequestSource) ?? "",
        }));

    const convertFiltersToParams = useCallback((filters?: FilterOption<MappedDeviceMediaRequest>[]) => {
        const params: {
            status?: MediaRequestStatuses;
            source?: MediaRequestSource;
            dateFrom?: Date;
            dateTo?: Date;
        } = {};

        if (!filters) return params;

        filters.forEach((filter) => {
            if (filter.value !== undefined && filter.value !== "") {
                switch (filter.id) {
                    case "status":
                        params.status = parseInt(filter.value) as MediaRequestStatuses;
                        break;
                    case "mediaRequestSource":
                        params.source = parseInt(filter.value) as MediaRequestSource;
                        break;
                    case "requestDate":
                        if (filter.value && typeof filter.value === "object") {
                            if (filter.value.from) params.dateFrom = new Date(filter.value.from);
                            if (filter.value.to) params.dateTo = new Date(filter.value.to);
                        }
                        break;
                }
            }
        });

        return params;
    }, []);

    const fetchRequests = useCallback(
        async (reset = false, cursor?: string) => {
            if (!device?.id) return;

            setIsLoading(true);
            try {
                const filterParams = convertFiltersToParams(query.filters);

                const response = await MediaRequests.getRequestsInfiniteScroll(device.id, query.limit, query.sortKey, query.sortDirection, filterParams.status, filterParams.source, filterParams.dateFrom, filterParams.dateTo, cursor || query.cursor);

                if (reset) {
                    setRequests(response?.data || []);
                } else {
                    setRequests((prev) => {
                        const existingIds = new Set(prev.map((req) => req.id));
                        const newRequests = (response?.data || []).filter((req) => !existingIds.has(req.id));
                        return [...prev, ...newRequests];
                    });
                }

                setHasMore(response?.hasMore || false);
                setNextCursor(response?.nextCursor);
            } catch (error) {
                console.error("Failed to fetch media requests:", error);
                toastWarning("Failed to load media requests. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        },
        [device, query, convertFiltersToParams]
    );

    useEffect(() => {
        const debouncedFetch = debounce(() => fetchRequests(true), 300);
        debouncedFetch();
    }, [fetchRequests]);

    const handleAdd = useCallback(() => {
        if (!device) {
            return;
        }

        const newRequest = new DeviceMediaRequest();
        const now = startOfMinute(new Date()); // truncate default period to :00 seconds
        newRequest.periodStart = subHours(now, 1);
        newRequest.periodEnd = now;
        newRequest.deviceId = device!.id!;
        newRequest.includeVideo = true;

        editDialog.show(
            async (request) => {
                if (request.id?.length) {
                    throw new Error("Cannot update Media Requests");
                } else {
                    await MediaRequests.createRequest(device?.id, request as DeviceMediaRequest);
                    toastInfo("Media Request Created", `Media requested for Device ${device.name ?? device.deviceKey} from ${formatInTimeZone(request.periodStart!, facilityTZ!, "Pp")} to ${formatInTimeZone(request.periodEnd!, facilityTZ!, "Pp (zzz)")}`);
                }
                if (onChange && device) await onChange(device);
                fetchRequests(true);
            },
            "Add Record Request",
            newRequest
        );
    }, [device, editDialog, onChange, facilityTZ, fetchRequests]);

    const handleDelete = useCallback(
        (existingRecord?: DeviceMediaRequest | undefined) => {
            confirm.show("Delete Record Request?", `Are you sure you want to delete this request ?`, "Delete", async () => {
                await MediaRequests.deleteRequest(device?.id, existingRecord?.id);
                if (onChange && device) await onChange(device);
                fetchRequests(true);
            });
        },
        [confirm, device, onChange, fetchRequests]
    );

    const handleSort = (sortKey: string, direction: "asc" | "desc") => {
        const sortKeyEnum = sortKey === "requestDate" ? MediaRequestSortKey.RequestDate : sortKey === "status" ? MediaRequestSortKey.Status : sortKey === "expireDate" ? MediaRequestSortKey.ExpireDate : MediaRequestSortKey.RequestDate;

        setQuery((prev) => ({
            ...prev,
            sortKey: sortKeyEnum,
            sortDirection: direction,
            cursor: undefined,
        }));
        setNextCursor(undefined);
        setHasMore(true);
        setRequests([]);
        setIsLoading(true);
    };

    const handleFilter = (filters: FilterOption<MappedDeviceMediaRequest>[]) => {
        setQuery((prev) => ({
            ...prev,
            filters,
            cursor: undefined,
        }));
        setNextCursor(undefined);
        setHasMore(true);
        setRequests([]);
        setIsLoading(true);
    };

    const handleLoadMore = useCallback(async () => {
        if (hasMore && !isLoading && nextCursor) {
            await fetchRequests(false, nextCursor);
        }
    }, [hasMore, isLoading, nextCursor, fetchRequests]);

    const getStatusHoverText = (d: DeviceMediaRequest): ReactNode => {
        if (d.status === MediaRequestStatuses.Uploading) {
            let eta = "";
            if (d.statusStart && d.progress > 0) {
                const timeSinceStart = Date.now() - d.statusStart!.getTime();
                const timeAtFinish = (1.0 / d.progress) * timeSinceStart;
                eta = `ETA: ${formatRelative(addMilliseconds(d.statusStart, timeAtFinish), d.statusStart)}`;
            }
            return eta;
        } else if (d.status === MediaRequestStatuses.Scheduled) {
            return d.periodEnd! < new Date() ? "Requested" : "Scheduled";
        } else if (
            d.status === MediaRequestStatuses.Merging ||
            d.status === MediaRequestStatuses.MergingVideo ||
            d.status === MediaRequestStatuses.MergingRadar
        ) {
            return "Processing";
        } else if (d.status === MediaRequestStatuses.PartiallyComplete) {
            return "Partially complete";
        }
        return getMediaRequestStatusesDisplayName(d.status);
    };

    const getStatusText = (d: DeviceMediaRequest): ReactNode => {
        if (d.status === MediaRequestStatuses.Uploading) {
            const progress = `${getMediaRequestStatusesDisplayName(d.status)}: ${Math.round(d.progress * 100)}%`;
            const rate = d.bytesPerSecond ? ` at ${formatByteSize(d.bytesPerSecond ?? 0)}/s` : '';
            return progress + rate;
        } else if (d.status === MediaRequestStatuses.Scheduled) {
            return d.periodEnd! < new Date() ? "Requested" : "Scheduled";
        } else if (
            d.status === MediaRequestStatuses.Merging ||
            d.status === MediaRequestStatuses.MergingVideo ||
            d.status === MediaRequestStatuses.MergingRadar
        ) {
            return "Processing";
        } else if (d.status === MediaRequestStatuses.PartiallyComplete) {
            return "Partially complete";
        }
        return getMediaRequestStatusesDisplayName(d.status);
    };

    // Color the status dot by lifecycle status AND result quality: a Complete clip that carries a
    // warning notice (gaps / approximate) goes amber, and PartiallyComplete is amber rather than the
    // old misleading green. An Error-severity notice (e.g. no-footage) reads red even pre-terminal.
    const statusConvert = (d: DeviceMediaRequest): "ok" | "warn" | "error" | undefined => {
        const severity = maxNoticeSeverity(visibleNotices(d.notices, isSuperAdmin));
        if (d.status === MediaRequestStatuses.Error || severity === "Error") {
            return "error";
        } else if (
            d.status === MediaRequestStatuses.PartiallyComplete ||
            d.status === MediaRequestStatuses.Cancelled ||
            d.status === MediaRequestStatuses.Expired ||
            severity === "Warning"
        ) {
            return "warn";
        }
        return "ok";
    };

    //In-progress states spin; terminal states take a severity icon driven
    // by status AND result quality (a Complete clip with a warning notice shows the amber triangle, not a
    // green check). The notice TEXT itself lives in the Result column, visible without hovering.
    const statusIconFor = (d: DeviceMediaRequest): { Icon: IconType; className: string } => {
        switch (d.status) {
            case MediaRequestStatuses.Uploading:
            case MediaRequestStatuses.Recording:
            case MediaRequestStatuses.Merging:
            case MediaRequestStatuses.MergingVideo:
            case MediaRequestStatuses.MergingRadar:
                return { Icon: Icons.Spinner, className: "text-brand-primary animate-spin" };
            case MediaRequestStatuses.Scheduled:
                return { Icon: Icons.Clock, className: "text-gray-400" };
            case MediaRequestStatuses.Cancelled:
                return { Icon: Icons.Times, className: "text-gray-400" };
            case MediaRequestStatuses.Expired:
                return { Icon: Icons.Clock, className: "text-gray-400" };
        }
        const quality = statusConvert(d);
        if (quality === "error") return { Icon: Icons.CircleError, className: "text-red-600" };
        if (quality === "warn") return { Icon: Icons.AlertTriangle, className: "text-amber-500" };
        return { Icon: Icons.CircleSuccess, className: "text-green-600" };
    };

    const getSortKeyDataKey = (sortKey?: MediaRequestSortKey): string | undefined => {
        switch (sortKey) {
            case MediaRequestSortKey.RequestDate:
                return "requestDate";
            case MediaRequestSortKey.Status:
                return "status";
            case MediaRequestSortKey.ExpireDate:
                return "expireDate";
            default:
                return undefined;
        }
    };

    const infiniteScrollOptions: InfiniteScrollOptions = {
        enabled: true,
        onLoadMore: handleLoadMore,
        hasMore,
        isLoading,
        overscan: 1,
        followOutput: false,
    };

    const allFilters: FilterOption<MappedDeviceMediaRequest>[] = useMemo(() => {
        const columnFilters: FilterOption<MappedDeviceMediaRequest>[] = [
            {
                id: "status",
                label: "Status",
                filterFn: (data: MappedDeviceMediaRequest, value: any) => {
                    if (value === undefined || value === "") return true;
                    return String(data.status) === String(value);
                },
                type: "select",
                options: statusFilterOptions,
            },
            {
                id: "mediaRequestSource",
                label: "Source",
                filterFn: (data: MappedDeviceMediaRequest, value: any) => {
                    if (value === undefined || value === "") return true;
                    return String(data.mediaRequestSource) === String(value);
                },
                type: "select",
                options: sourceFilterOptions,
            },
            {
                id: "requestDate",
                label: "Requested On",
                filterFn: (data: MappedDeviceMediaRequest, value: any) => {
                    if (value === undefined || value === "") return true;
                    const dataDate = data.requestDate ? new Date(data.requestDate) : null;
                    if (!dataDate) return false;

                    const fromDate = value.from ? new Date(value.from) : null;
                    const toDate = value.to ? new Date(value.to) : null;

                    if (fromDate && dataDate < fromDate) return false;
                    if (toDate && dataDate > toDate) return false;

                    return true;
                },
                type: "daterange",
            },
        ];
        return columnFilters;
    }, [statusFilterOptions, sourceFilterOptions]);

    return (
        <>
            <DataInfoCard Icon={Icons.Filter} title="Earliest Video on Device">
                {earliestHelper.isLoading || isFacilityTZLoading ? "Loading..." : formatInTimeZone(new Date(earliest?.start!), facilityTZ!, "MMM d  HH:mm zzz")}
            </DataInfoCard>
            <PageContainer
                title="Media Requests"
                titleRight={
                    <IconLink Icon={Icons.PlusCircle} onClick={handleAdd}>
                        Request Media
                    </IconLink>
                }
            >
                <SortableTable
                    mode="infiniteScroll"
                    isLoading={isLoading}
                    initialFilters={allFilters}
                    columns={[
                        {
                            header: "Status",
                            dataKey: "status",
                            renderFunc: (d) => {
                                const { Icon, className } = statusIconFor(d!);
                                return (
                                    <div className="inline-flex items-center gap-2">
                                        <Icon className={`h-4 w-4 shrink-0 ${className}`} aria-hidden />
                                        <HoverTooltip hoverable={d?.status !== undefined && <span>{getStatusText(d)}</span>}>{getStatusHoverText(d!)}</HoverTooltip>
                                    </div>
                                );
                            },
                            width: 150,
                            sortable: true,
                            filterable: true,
                            filterType: "select",
                            filterOptions: statusFilterOptions,
                        },
                        {
                            header: "ID",
                            renderFunc: (d) =>
                                d?.id ? (
                                    <div className="inline-flex items-center gap-1 whitespace-nowrap">
                                        {/* The id doubles as the way in to the raw request: the list
                                            columns cannot show every field, and the ones an integrator
                                            cares about (the delivered clip window, notices, expiry)
                                            have no column of their own. */}
                                        <MediaRequestJsonLink request={d} />
                                        <IconLink
                                            Icon={Icons.Clipboard}
                                            onClick={() => {
                                                copy(d.id!);
                                                toastSuccess("Copied", undefined, 1500);
                                            }}
                                        />
                                    </div>
                                ) : null,
                            width: 90,
                        },
                        {
                            header: "Requested By",
                            renderFunc: (d) => {
                                if (d?.mediaRequestSource === MediaRequestSource.System) return <span>System</span>;
                                if (d?.mediaRequestSource === MediaRequestSource.Device) return <span>Device</span>;
                                const orgUser = orgUsers?.find((x) => x.id === d?.requestedById);
                                if (orgUser) return <span>{orgUser.name}</span>;                             
                                const superUser = allUsers?.find((x) => x.id === d?.requestedById && x.isSuperAdmin);
                                if (superUser) {
                                    const name = superUser.firstName?.length ? `Admin (${superUser.firstName ?? ""})` : null;
                                    return <span>{name || superUser.email}</span>;
                                }
                                return <span>Administrator</span>;
                            },
                            width: 150,
                        },
                        {
                            header: "Requested On",
                            renderFunc: (d) =>
                                facilityTZ && d?.requestDate ? (
                                    <HoverTooltip hoverable={<span>{formatInTimeZone(d.requestDate, facilityTZ, "MMMM d")}</span>}>
                                        {d.requestDate.toISOString()}
                                    </HoverTooltip>
                                ) : null,
                            dataKey: "requestDate",
                            width: 100,
                            filterable: true,
                            filterType: "daterange",
                        },
                        {
                            header: "Recording Period",
                            renderFunc: (d) =>
                                facilityTZ && (
                                    <HoverTooltip
                                        hoverable={
                                            <>
                                                <span>{formatRecordingPeriodDuration(d!.periodStart!, d!.periodEnd!)}</span>
                                                <span> starting {formatInTimeZone(d!.periodStart!, facilityTZ, "MMM d, HH:mm zzz")}</span>
                                            </>
                                        }
                                    >
                                        <div className="flex flex-col">
                                            <span>{d?.periodStart?.toISOString()}</span>
                                            <span>to</span>
                                            <span>{d?.periodEnd?.toISOString()}</span>
                                        </div>
                                    </HoverTooltip>
                                ),
                            width: 200,
                        },
                        {
                            header: "Result",
                            width: 260,
                            renderFunc: (d) => d && <MediaRequestDownloadLinks request={d} />,
                        },
                        {
                            header: "Expires",
                            dataKey: "expireDate",
                            renderFunc: (d) =>
                                d?.status === MediaRequestStatuses.Complete && d?.expireDate && facilityTZ ? (
                                    <HoverTooltip
                                        hoverable={
                                            <span>
                                                {formatRelative(utcToZonedTime(d.expireDate, facilityTZ), utcToZonedTime(new Date(), facilityTZ))} {formatInTimeZone(d.expireDate, facilityTZ, "zzz")}
                                            </span>
                                        }
                                    >
                                        {d.expireDate.toISOString()}
                                    </HoverTooltip>
                                ) : null,
                            width: 140,
                        },
                        {
                            header: "",
                            width: 50,
                            renderFunc: (recordingRecord) => (
                                <IconLink Icon={Icons.Trash} onClick={() => handleDelete(recordingRecord)}>
                                    Delete
                                </IconLink>
                            ),
                        },
                    ]}
                    data={requests?.map((request) => ({
                        ...request,
                        id: request.id || request.requestDate?.toString() || "",
                    }))}
                    emptyValue={<div className="my-5">No recordings found for this device</div>}
                    filtering
                    onSort={handleSort}
                    onFilter={handleFilter}
                    sortKey={getSortKeyDataKey(query.sortKey)}
                    sortDirection={query.sortDirection}
                    infiniteScroll={infiniteScrollOptions}
                />
            </PageContainer>
            {confirm.renderDialog()}
            {facilityTZ && editDialog.renderDialog((model, helpers) => {
                return (<EditDeviceMediaRequest zone={facilityTZ} request={model} minStart={earliest?.start} maxEnd={device?.enableDvrRecording === false ? earliest?.end : undefined} helpers={helpers} device={device} />);
            })
            }
        </>
    );
};
